import { useAppDispatch, useAppSelector } from '../app/hooks';
import { selectLegacyToken, selectLongTermJWT, selectShortTermJWT } from '../app/slices/session.slice';
import { selectBankIntegrationVendor, selectBankRedirectMethod } from '../app/slices/configuration.slice';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material';
import { Language } from '../app/slices/customizations.slice';
import { BankAppConfiguration } from '@fncrdbl/bank-sdk/dist/esm/types/sdk';
import { bankAppCompleted } from '../app/thunks/bank-process-completed';
import { AxiosInstance, HttpStatusCode } from 'axios';
import { useCallback } from 'react';
import { attachMaintenanceModeInterceptor, attachRetryInterceptor } from '../api/response.interceptor';
import { submitError } from '../app/thunks/submit-error';
import NewRelicAgent from '../newrelic';

const URL = process.env['REACT_APP_API_URL'] as string;

export const useBankAppConfiguration = (): BankAppConfiguration => {
	const shortTermToken = useAppSelector(selectShortTermJWT);
	const longTermToken = useAppSelector(selectLongTermJWT);
	const inviteToken = useAppSelector(selectLegacyToken);
	const bankRedirectMethod = useAppSelector(selectBankRedirectMethod);
	const bankIntegrationVendor = useAppSelector(selectBankIntegrationVendor);
	const language = useLanguage();

	const theme = useTheme();
	const dispatch = useAppDispatch();

	const setupApiClient = useCallback((axiosInstance: AxiosInstance) => {
		const id = attachRetryInterceptor(axiosInstance, {
			skipStatusCodes: [HttpStatusCode.NotFound, HttpStatusCode.BadRequest]
		});

		const maintenanceModeErrorId = attachMaintenanceModeInterceptor(axiosInstance, dispatch);

		return () => {
			axiosInstance.interceptors.response.eject(maintenanceModeErrorId);
			axiosInstance.interceptors.response.eject(id);
		};
	}, []);

	const callbacks = {
		onFinished: () => {
			dispatch(bankAppCompleted());
		},
		onClose: () => {
			dispatch(bankAppCompleted());
		},
		onError: (error: any) => {
			NewRelicAgent.noticeError(error);
			dispatch(submitError(error));
		}
	};

	return {
		shortTermToken,
		longTermToken,
		inviteToken,
		url: URL,
		bankRedirectMethod,
		setupApiClient: setupApiClient as BankAppConfiguration['setupApiClient'],
		bankIntegrationVendor,
		callbacks,
		language: language as Language,
		primaryColor: theme.palette.primary.light,
		secondaryColor: theme.palette.primary.lighter,
		autoClose: true
	};
};

const useLanguage = () => {
	const {
		i18n: { language }
	} = useTranslation();

	return language as Language;
};
